import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminApiClient } from '../utils/adminApiClient';

interface Role {
    id: string;
    ident: string;
    name: string;
    clientId?: string;
}

interface ClientRoleManagementProps {
    clientId: string;
    clientName: string;
}

export const ClientRoleManagement: React.FC<ClientRoleManagementProps> = ({
    clientId,
    clientName
}) => {
    const [clientRoles, setClientRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [newRole, setNewRole] = useState({
        name: '',
        ident: ''
    });
    const { enqueueSnackbar } = useSnackbar();

    const loadRoles = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Load only client roles for this specific client
            const clientRolesData = await adminApiClient.getClientRoles(clientId);
            setClientRoles(clientRolesData);
            
        } catch (error) {
            console.error('Error loading client roles:', error);
            setError('Fehler beim Laden der Client-Rollen');
            enqueueSnackbar('Fehler beim Laden der Client-Rollen', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRoles();
    }, [clientId]);

    const handleCreateRole = async () => {
        try {
            await adminApiClient.createClientRole(clientId, newRole);
            await loadRoles();
            setCreateDialogOpen(false);
            setNewRole({ name: '', ident: '' });
            enqueueSnackbar('Client-Rolle erfolgreich erstellt', { variant: 'success' });
        } catch (error) {
            console.error('Error creating client role:', error);
            enqueueSnackbar('Fehler beim Erstellen der Client-Rolle', { variant: 'error' });
        }
    };

    const handleDeleteRole = async () => {
        if (!selectedRole) return;

        try {
            await adminApiClient.deleteClientRole(clientId, selectedRole.ident);
            await loadRoles();
            setDeleteDialogOpen(false);
            setSelectedRole(null);
            enqueueSnackbar('Client-Rolle erfolgreich gelöscht', { variant: 'success' });
        } catch (error) {
            console.error('Error deleting client role:', error);
            enqueueSnackbar('Fehler beim Löschen der Client-Rolle', { variant: 'error' });
        }
    };

    const handleRefresh = () => {
        loadRoles();
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Client-Rollen
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Verwalten Sie die Rollen für diese Client-Anwendung
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                        size="small"
                    >
                        Aktualisieren
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        size="small"
                    >
                        Neue Rolle
                    </Button>
                </Box>
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : clientRoles.length === 0 ? (
                <Alert severity="info">
                    Keine Client-Rollen vorhanden. Erstellen Sie Ihre erste Rolle.
                </Alert>
            ) : (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 2
                }}>
                    {clientRoles.map((role) => (
                        <Card variant="outlined" key={role.id}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="medium">
                                            {role.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            <code>{role.ident}</code>
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            setSelectedRole(role);
                                            setDeleteDialogOpen(true);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}


            {/* Create Role Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Neue Client-Rolle für {clientName} erstellen</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Rollen-Name"
                            value={newRole.name}
                            onChange={(e: any) => setNewRole({ ...newRole, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Rollen-Identifikator"
                            value={newRole.ident}
                            onChange={(e: any) => setNewRole({ ...newRole, ident: e.target.value })}
                            helperText="Eindeutiger Identifikator für die Rolle (z.B. 'editor', 'viewer', 'admin')"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
                    <Button 
                        onClick={handleCreateRole} 
                        variant="contained"
                        disabled={!newRole.name || !newRole.ident}
                    >
                        Erstellen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Role Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Client-Rolle löschen</DialogTitle>
                <DialogContent>
                    <Typography>
                        Möchten Sie die Client-Rolle "{selectedRole?.name}" für {clientName} wirklich löschen?
                        Diese Aktion kann nicht rückgängig gemacht werden.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleDeleteRole} color="error" variant="contained">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
